import { Stream } from "@cloudflare/stream-react";
import { useEffect, useRef, useState } from "react";
import { fetchGetFolder, fetchGetVideo, fetchUpdateUserVideoStatistics } from "./FolderApi";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { showError } from "../../features/Error/errorSlice";
import { useLocation, useParams } from "react-router-dom";
import { setLoading } from "../../services/global/globalSlice";
import { selectTranslations } from "../../services/i18n/i18nSlice";
import { TitleH2 } from "../../components/TitleH2";

import correct_img from '../../assets/images/folders/correct.svg'
import incomplete_img from '../../assets/images/ofa/incomplete_folder.svg'

import FolderTopBar from "./_components/FolderTopBar";
import debounce from "lodash.debounce";
import Skeleton from "react-loading-skeleton";
import { Spinner } from "react-bootstrap";
import VideoCompletedModal from "./_components/VideoCompletedModal";
import { TENANT } from "../../config/constants";
import { GetFromLocalStorage, SaveToLocalStorage } from "../../utils/utils";


export default function VideoPlayer() {

    const [videoData, setVideoData] = useState(null)
    const [folderData, setFolderData] = useState(null)
    const [videoModalShow, setVideoModalShow] = useState(false)
    const dispatch = useDispatch()

    const videoRef = useRef()
    const maxWatchedTimeRef = useRef(0) // Traccia la posizione massima raggiunta
    const isSeekingBackRef = useRef(false) // Flag per evitare loop infiniti
    const lastTimeBeforeSeekRef = useRef(0) // Posizione prima del seek
    const isSeekingRef = useRef(false) // Flag per sapere se è in corso un seek

    const location = useLocation()
    const {video_id, folder_id} = useParams()

    const t = useSelector(selectTranslations)
    
    const loadVideoData = async () => {
        const response = await fetchGetVideo({video_id: video_id})
        if(response.success){
            setVideoData(response.data)
        } else {
            dispatch(showError({
                title: t.folder.error.loading_video,
                text: t.folder.error.loading_video_text
            }))  
        }
    }

    const loadFolderData = async () => {
        const response = await fetchGetFolder({...(folder_id && {folder_id: folder_id})})
        if (response.success){
            setFolderData(response.data)
            dispatch(setLoading(false))
        } else {
            toast.error(t.training.error_loading_folder)
            dispatch(setLoading(false))
        }
    }

    const handleTrackVideoEnd = async (e) => {
        console.log(e);
        const response = await fetchUpdateUserVideoStatistics({
            video_id: video_id,
            completed: videoData?.completed || videoRef.current?.ended
        })
        if(!response.success)
            console.error(response.error);
        const response_video = await fetchGetVideo({video_id: video_id})
        if(response_video.success){
            setVideoData({
                ...videoData,
                completed: response_video.data.completed
            })
        } 
    }

    const _handleTrackVideoProgress = async () => {
        const response = await fetchUpdateUserVideoStatistics({
            video_id: video_id,
            completed: videoData?.completed || videoRef.current?.ended,
            seconds_elapsed: videoRef.current?.currentTime || 0
        })
        if(!response.success)
            console.error(response.error);
        else if(response?.success && (videoData?.completed || videoRef.current?.ended)){
            // Controlla se il modal è già stato mostrato per questo video
            const storageKey = `${TENANT}_video_completed_modal_shown`;
            const shownVideos = GetFromLocalStorage(storageKey) || {};
            
            if (!shownVideos[video_id]) {
                setVideoModalShow(true);
            }
        }
    }

    const handleTrackVideoProgress = debounce(_handleTrackVideoProgress, 300)

    const handleCloseVideoModal = () => {
        // Salva nel localStorage che il modal è stato mostrato per questo video
        const storageKey = `${TENANT}_video_completed_modal_shown`;
        const shownVideos = GetFromLocalStorage(storageKey) || {};
        shownVideos[video_id] = true;
        SaveToLocalStorage(storageKey, shownVideos);
        
        setVideoModalShow(false);
    };

    // Verifica se la cartella è di tipo learning_path
    const isLearningPath = () => {
        return folderData?.type === 'learning_path' || 
               folderData?.parent_folder_details?.type === 'learning_path';
    };

    // Cattura la posizione PRIMA che inizi il seek
    const handleSeeking = (e) => {
        if (!isSeekingRef.current) {
            lastTimeBeforeSeekRef.current = videoRef.current?.currentTime || 0;
            isSeekingRef.current = true;
            console.log("Seeking started from:", lastTimeBeforeSeekRef.current);
        }
    };

    // Previene il seek in avanti se il video non è completato E la cartella è learning_path
    const handleSeeked = (e) => {
        isSeekingRef.current = false;

        // Ignora se stiamo riportando indietro il video noi stessi
        if (isSeekingBackRef.current) {
            isSeekingBackRef.current = false;
            lastTimeBeforeSeekRef.current = videoRef.current?.currentTime || 0;
            return;
        }

        const currentTime = videoRef.current?.currentTime || 0;
        
        console.log("Seeked to:", currentTime, "Last position before seek:", lastTimeBeforeSeekRef.current, "Max watched:", maxWatchedTimeRef.current, "Completed:", videoData?.completed, "Is learning path:", isLearningPath());
        
        // Se il video è già completato, permetti qualsiasi seek
        if (videoData?.completed) {
            lastTimeBeforeSeekRef.current = currentTime;
            return;
        }

        // Blocca il seek SOLO se la cartella è di tipo learning_path
        if (!isLearningPath()) {
            lastTimeBeforeSeekRef.current = currentTime;
            return;
        }

        // Controlla se l'utente ha saltato AVANTI oltre la posizione massima raggiunta
        if (currentTime > maxWatchedTimeRef.current + 1) { // tolleranza di 1 secondo
            console.log("Blocking seek! Resetting to:", maxWatchedTimeRef.current);
            isSeekingBackRef.current = true; // Imposta flag per evitare loop
            videoRef.current.currentTime = maxWatchedTimeRef.current;
            toast.info(t?.folder?.video_seek_blocked || "Completa prima questa parte del video");
        } else {
            // Aggiorna l'ultima posizione valida
            lastTimeBeforeSeekRef.current = currentTime;
        }
    };

    // Aggiorna la posizione massima raggiunta durante la riproduzione normale
    const handleTimeUpdateWithSeekControl = (e) => {
        const currentTime = videoRef.current?.currentTime || 0;
        
        // Aggiorna il massimo SOLO se NON siamo in un seek e stiamo procedendo linearmente
        if (!isSeekingRef.current && currentTime > maxWatchedTimeRef.current) {
            maxWatchedTimeRef.current = currentTime;
            console.log("Max watched updated to:", maxWatchedTimeRef.current);
        }

        // Chiama il tracking normale
        handleTrackVideoProgress(e);
    };

    useEffect(()=>{
        if(video_id)
            loadVideoData()
    },[video_id])

    useEffect(()=>{
        if(location.state?.folderData)
            setFolderData(location.state?.folderData)
        else    
            loadFolderData()
    },[location])

    // Inizializza maxWatchedTime quando carichi i dati del video
    useEffect(() => {
        if (videoData?.seconds_elapsed) {
            maxWatchedTimeRef.current = videoData.seconds_elapsed;
        }
        // Se il video è completato, permetti di seekare ovunque
        if (videoData?.completed && videoRef.current?.duration) {
            maxWatchedTimeRef.current = videoRef.current.duration;
        }
    }, [videoData]);

    const videoFolderData = folderData?.videos?.find(video => video?.file_id === video_id)

    return(
        <section className="content_section">
            {
                folderData ? <FolderTopBar current={true} folderData={folderData} t={t} /> : null
            }
            
            <div className="mt-5">
                {
                    videoData ?
                        <Stream 
                            controls={true} 
                            streamRef={videoRef}
                            poster={videoData?.poster_image_url}
                            src={videoData?.uid} 
                            currentTime={videoData?.seconds_elapsed}
                            onEnded={handleTrackVideoEnd}
                            onTimeUpdate={handleTimeUpdateWithSeekControl}
                            onSeeking={handleSeeking}
                            onSeeked={handleSeeked}
                            className="video_player"
                        />
                    : <div className="video_loader">
                        <Spinner />
                    </div>
                }
                {
                    folderData ? 
                        <div className="d-flex align-items-center mt-4">
                            <img src={!videoData?.completed ? incomplete_img : correct_img} className='ms-1' alt="" />
                            <TitleH2 className="mb-0 ms-1">{videoFolderData?.name}</TitleH2>
                        </div>
                    : null
                }
            </div>
            <VideoCompletedModal
                show={videoModalShow}
                onClose={handleCloseVideoModal}
                folderData={folderData}
            />
        </section>
    )
}